import { AppNode, NodeType, isNotGroupNode } from "@features/builder/graph/types"
import { InternalNode } from "@xyflow/react"

export const getParentsList = (node: AppNode | InternalNode<AppNode>): string[] => {
  if (!isNotGroupNode(node)) return []

  if (
    node.type === NodeType.Table ||
    node.type === NodeType.ForecastTable ||
    node.type === NodeType.ScenarioTable
  ) {
    return node.data.originalAttribute.properties.parents_lst ?? []
  }

  return node.data?.originalNode?.properties.parents_lst ?? []
}

/**
 * Whether any node in `nodes` lists `nodeId` as a parent.
 *
 * Kept next to `getParentsList` so worker threads can use it without pulling in
 * `@features/builder/helpers/nodes`, which imports React modules.
 */
export const hasChildNodes = (
  nodeId: string,
  nodes: (AppNode | InternalNode<AppNode>)[],
): boolean => nodes.some((node) => getParentsList(node).includes(nodeId))

/**
 * Ids of every node that at least one other node lists as a parent.
 *
 * Same answer as `hasChildNodes`, built once for the whole graph. Use this when asking
 * the question for every node — `hasChildNodes` in a loop walks all nodes each time,
 * which is quadratic on a large graph.
 */
export const getNodeIdsWithChildren = (
  nodes: (AppNode | InternalNode<AppNode>)[],
): ReadonlySet<string> => {
  const ids = new Set<string>()

  for (const node of nodes) {
    for (const parentId of getParentsList(node)) ids.add(parentId)
  }

  return ids
}
